import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import Toggle from 'react-toggle';

import Axios from 'axios';
import { injectStateService, StateService } from '@/shared/services/StateService';
import AuthService from '@/shared/services/AuthService';
import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import Button from '@/shared/components/Button';
import { AddIcon } from '@/shared/components/Icons';
import { absenceRegistrationOptions } from '@/shared/components/registration/registrationHelpers';
import RegistrationModal from '@/shared/components/registration/RegistrationModal';
import { CanCreate } from '@/shared/components/security';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import moment from 'moment';
import DashboardTimeTile from './partials/DashboardTimeTile';
import userStorage from '@/shared/services/userStorage';

import messages from '../dashboardMessages';
import DashboardAbsenceTile from './partials/DashboardAbsenceTile';

const InDaysStorageKey = 'AbsenceDashboardInDays';
const AbsenceDashboard = ({ intl, stateService }) => {
  const [data, setData] = useState(null);
  const [showRegistrationModal, setRegistrationModal] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  const [inDays, setInDays] = useState(userStorage.getItem(InDaysStorageKey) ?? true);

  const currentUserId = AuthService.getUserId();
  const hasRegistrationPermission = AuthService.hasPermission({
    functionality: 'RegistrationOwn',
    privilege: 'Create',
  });

  const createRegistration = type => {
    setRegistrationModal({
      registration: { type, currentUserId, date: moment.utc().toDate() },
      registrationOptions: type === 3 || type === 4 ? absenceRegistrationOptions : {},
    });
  };

  useEffect(() => {
    const cancelToken = Axios.CancelToken.source();

    api.base
      .get(`${config.appUrls.api}/api/AbsenceCounter/Widget/`, {
        cancelToken: cancelToken.token,
      })
      .then(response => {
        setData(response.data || {});
      }, handleHttpError(intl));
    return () => {
      if (cancelToken) cancelToken.cancel('Operation canceled due to new request.');
    };
  }, [intl, reloadCount]);
  useEffect(() => {
    userStorage.setItem(InDaysStorageKey, inDays);
  }, [inDays]);
  const openRegOverview = useCallback(
    period => {
      const currentUserId = AuthService.getUserId();
      stateService.gotoRegistrationOverview({ period, userId: currentUserId });
    },
    [stateService],
  );

  return (
    <div className="absence-dashboard">
      <div className="title-block">
        <FormattedMessage {...messages.AbsenceCounters} />
        <ZendeskHelp article="13956867188764" className="margin10R" />
      </div>
      <div className="content-block">
        {!data ? (
          <LoadingAnimation />
        ) : data.length > 0 ? (
          <div className="tile-grid">
            {data.map(x => (
              <DashboardAbsenceTile key={x.resource} data={x} intl={intl} inDays={inDays} />
            ))}
          </div>
        ) : (
          <span>{intl.formatMessage(messages.NoAbsencesFound)}</span>
        )}
      </div>
      <div className="bottom-block">
        {hasRegistrationPermission ? (
          <Button btnClassName="btn btn-sm btn-inverse" onClick={() => createRegistration(3)}>
            <AddIcon />
            <span>{intl.formatMessage(messages.AddAbsence)}</span>
          </Button>
        ) : null}
        <span className="pull-right">
          <label className="react-toggle-wrapper margin5T">
            <Toggle checked={inDays} onChange={e => setInDays(e.target.checked)} />
            <FormattedMessage className="label-text" {...messages.ShowAbsencesInDays} />
          </label>
        </span>
      </div>

      {showRegistrationModal && (
        <RegistrationModal
          {...showRegistrationModal}
          show
          onSave={() => {
            setRegistrationModal(null);
            setReloadCount(reloadCount + 1);
          }}
          onClose={() => setRegistrationModal(null)}
        />
      )}
    </div>
  );
};
AbsenceDashboard.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};

export default injectStateService(injectIntl(AbsenceDashboard));
